/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/HorizontalScrollButtons" />
import * as React from "react";
import { PropsWithChildren, useCallback, useLayoutEffect, useRef } from "react";
import { useObservable } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";

const smoothScrollDurationMs = 200;
const defaultScollAmountPx = 150;

export function HorizontalScrollButtons({ scrollAmountPx, children }: PropsWithChildren<{ scrollAmountPx?: number }>) {
    scrollAmountPx ??= defaultScollAmountPx;

    const outer = useRef<HTMLDivElement | null>(null);
    const [showScrollLeftButton] = useObservable(false);
    const [showScrollRightButton] = useObservable(false);

    const reEvaluateButtonStatus = useCallback(() => {
        if (!outer.current) {
            if (showScrollLeftButton.value) {
                showScrollLeftButton.value = false;
            }
            if (showScrollRightButton.value) {
                showScrollRightButton.value = false;
            }
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = outer.current;

        const canScrollLeft = scrollLeft > 0;
        const canScrollRight = scrollLeft + clientWidth + 1 < scrollWidth;

        if (showScrollLeftButton.value !== canScrollLeft) {
            showScrollLeftButton.value = canScrollLeft;
        }

        if (showScrollRightButton.value !== canScrollRight) {
            showScrollRightButton.value = canScrollRight;
        }
    }, []);

    const scrollToTheLeft = useCallback(() => {
        outer.current?.scroll({ left: outer.current!.scrollLeft - defaultScollAmountPx, behavior: "smooth" });
        setTimeout(reEvaluateButtonStatus, smoothScrollDurationMs);
    }, [scrollAmountPx]);

    const scrollToTheRight = useCallback(() => {
        outer.current?.scroll({ left: outer.current!.scrollLeft + defaultScollAmountPx, behavior: "smooth" });
        setTimeout(reEvaluateButtonStatus, smoothScrollDurationMs);
    }, [scrollAmountPx]);

    useLayoutEffect(reEvaluateButtonStatus);

    return (
        <div className="horizontal-scroll-buttons flex-row">
            <Observer show={showScrollLeftButton}>
                {({ show }: { show: boolean }) => (
                    <Button
                        ariaLabel={Resources.ScrollLeft}
                        className={css("horizontal-scroll-button", !show && "hidden")}
                        iconProps={{ iconName: "ChevronLeft" }}
                        onClick={scrollToTheLeft}
                    />
                )}
            </Observer>

            <div className="scroll-container" ref={outer}>
                {children}
            </div>

            <Observer show={showScrollRightButton}>
                {({ show }: { show: boolean }) => (
                    <Button
                        ariaLabel={Resources.ScrollRight}
                        className={css("horizontal-scroll-button", !show && "hidden")}
                        iconProps={{ iconName: "ChevronRight" }}
                        onClick={scrollToTheRight}
                    />
                )}
            </Observer>
        </div>
    );
}
